import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import { Alert, Button, Dialog, DialogContent, Stack, TextField } from '@mui/material';

import stores, { Store } from '~/consts/stores';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import UsersService from '~/services/UsersService';

import getErrorMessage from '~/utils/getErrorMessage';

import { ALLOWED_BRANDS } from '../Users/UserDetails/SpoofUserBlock';

type Props = {
  open: boolean;
  onClose: () => void;
  userId: string;
  userLabel: string;
  orderUuid?: string;
};

export default function SpoofUserDialog({ open, onClose, userId, userLabel, orderUuid }: Props) {
  const { tenant } = useCurrentTenant();
  const { enqueueSnackbar } = useSnackbar();
  const [spoofUrl, setSpoofUrl] = useState<string | null>(null);
  const [storeName, setStoreName] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const flushSpoofingState = () => {
    setSpoofUrl(null);
    setStoreName(null);
    onClose();
  };

  const handleSpoofUser = async () => {
    try {
      setIsProcessing(true);
      const path = orderUuid ? `/account/my-escapes/order/${orderUuid}/` : '';
      const spoofUrl = await UsersService.spoofUser(userId, tenant, path);

      navigator.clipboard.writeText(spoofUrl);
      setSpoofUrl(spoofUrl);
      setStoreName(null);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'Failed to generate spoof link.'), { variant: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSpoofUserStore = async (storeKey: string) => {
    try {
      setIsProcessing(true);
      const path = orderUuid ? `/account/my-escapes/order/${orderUuid}/` : '';
      const store: Store = stores[storeKey];
      const spoofUrl = await UsersService.spoofUserStore(userId, tenant, path, store.utmMedium);

      navigator.clipboard.writeText(spoofUrl);
      setSpoofUrl(spoofUrl);
      setStoreName(store.displayName);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'Failed to generate spoof link.'), { variant: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onClose={flushSpoofingState}>
      <DialogContent>
        {spoofUrl ? (
          <Stack direction="column" spacing={2} sx={{ my: 2 }}>
            <Alert severity="warning" onClose={flushSpoofingState}>
              Extreme caution, paste into incognito only
            </Alert>
            <Alert severity="info" onClose={flushSpoofingState}>
              Your link has been copied to the clipboard!
            </Alert>
            <TextField
              InputLabelProps={{ shrink: true }}
              label={(storeName ? `${storeName} spoofing` : 'Spoofing') + ` link for ${userLabel}`}
              defaultValue={spoofUrl ?? ''}
              multiline
              disabled
            />
          </Stack>
        ) : (
          <Stack direction="column" spacing={2} sx={{ my: 2 }}>
            <Button variant="contained" color="success" onClick={handleSpoofUser} disabled={isProcessing}>
              Call centre link
            </Button>
            {ALLOWED_BRANDS.includes(tenant.brand) &&
              Object.entries(stores).map(([storeKey, store]) => (
                <Button
                  key={storeKey}
                  variant="contained"
                  color="success"
                  onClick={() => handleSpoofUserStore(storeKey)}
                  disabled={isProcessing}
                >
                  Spoof {store.displayName} link
                </Button>
              ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
